import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import type { TicketSettings, TicketSettingsUpdateInput } from '@cp3/shared';
import { useEffectiveUid, useIsAdminMode } from '../auth/TargetUidContext';

export function useTicketSettings() {
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useQuery<TicketSettings>({
    queryKey: ['ticket-settings', targetUid] as const,
    enabled: !!targetUid,
    staleTime: 30_000,
    queryFn: async () => {
      if (adminMode && targetUid) {
        const fn = httpsCallable<{ uid: string }, { settings: TicketSettings }>(
          functions,
          'adminGetTicketSettings',
        );
        const r = await fn({ uid: targetUid });
        return r.data.settings;
      }
      const fn = httpsCallable<unknown, { settings: TicketSettings }>(functions, 'getTicketSettings');
      const r = await fn({});
      return r.data.settings;
    },
  });
}

export function useUpdateTicketSettings() {
  const qc = useQueryClient();
  const targetUid = useEffectiveUid();
  const adminMode = useIsAdminMode();
  return useMutation({
    mutationFn: async (input: TicketSettingsUpdateInput) => {
      if (adminMode && targetUid) {
        const fn = httpsCallable<TicketSettingsUpdateInput & { uid: string }, { ok: true }>(
          functions,
          'adminSetTicketSettings',
        );
        const r = await fn({ ...input, uid: targetUid });
        return r.data;
      }
      const fn = httpsCallable<TicketSettingsUpdateInput, { ok: true }>(
        functions,
        'updateTicketSettings',
      );
      const r = await fn(input);
      return r.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ticket-settings', targetUid] }),
  });
}
